/**
 * M2O Relation Selector Interface
 * Allows selecting a related item from another collection (Many-to-One)
 */

import { useState, useMemo, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Edit, X, Search, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useItems } from '@/hooks/useItems';
import { useFields } from '@/hooks/useFields';
import { useCollection } from '@/hooks/useCollections';

interface M2ORelationSelectorProps {
    value: string | number | null;
    onChange: (value: string | number | null) => void;
    relatedCollection: string;
    displayTemplate?: string; // e.g., "{{name}}" or "{{id}} - {{title}}"
    disabled?: boolean;
    placeholder?: string;
    className?: string;
    enableCreate?: boolean;
    enableLink?: boolean;
}

export function M2ORelationSelector({
    value,
    onChange,
    relatedCollection,
    displayTemplate,
    disabled = false,
    placeholder = 'Select an item...',
    className,
    // enableCreate = false, // TODO: implement create functionality
    enableLink = false,
}: M2ORelationSelectorProps) {
    const [sheetOpen, setSheetOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const limit = 25;

    // Fetch related collection info
    const { data: collectionInfo } = useCollection(relatedCollection);

    // Fetch fields for the related collection
    const { fields, loading: fieldsLoading } = useFields({ collection: relatedCollection });

    // Get primary key field
    const primaryKeyField = useMemo(() => {
        return fields.find(f => f.schema?.is_primary_key)?.field || 'id';
    }, [fields]);

    // Get display fields (first few non-hidden fields)
    const displayFields = useMemo(() => {
        return fields
            .filter(f => !f.meta?.hidden && f.field !== primaryKeyField)
            .slice(0, 3)
            .map(f => f.field);
    }, [fields, primaryKeyField]);

    // Extract fields referenced in display template so we can fetch them
    const templateFields = useMemo(() => {
        if (!displayTemplate) return [];
        const matches = Array.from(displayTemplate.matchAll(/\{\{([\w.]+)\}\}/g));
        return matches.map((m) => m[1]).filter((f): f is string => !!f);
    }, [displayTemplate]);

    // Combine required fields for queries (deduped)
    const queryFields = useMemo(() => {
        const unique = new Set<string>([primaryKeyField, ...displayFields, ...templateFields]);
        return Array.from(unique);
    }, [primaryKeyField, displayFields, templateFields]);

    // Build query for fetching items
    const query = useMemo(() => ({
        fields: queryFields,
        limit,
        page,
        search: search || undefined,
    }), [queryFields, limit, page, search]);

    // Fetch items from related collection
    const { data: itemsData, isLoading: itemsLoading } = useItems(relatedCollection, query);

    // Fetch the currently selected item to display
    const selectedItemQuery = useMemo(() => ({
        fields: queryFields,
        filter: value ? { [primaryKeyField]: { _eq: value } } : undefined,
    }), [queryFields, value]);

    const { data: selectedItemData } = useItems(
        relatedCollection,
        selectedItemQuery,
        { enabled: !!value }
    );

    const selectedItem = selectedItemData?.items?.[0];
    const items = itemsData?.items || [];
    const totalCount = itemsData?.meta?.total_count || 0;
    const totalPages = Math.ceil(totalCount / limit);

    // Format display value using template
    const formatDisplayValue = useCallback((item: Record<string, unknown>): string => {
        if (!item) return '';

        if (displayTemplate) {
            // Replace {{field}} placeholders with actual values
            return displayTemplate.replace(/\{\{(\w+)\}\}/g, (_, field) => {
                return String(item[field] ?? '');
            });
        }

        // Default: show primary key and first display field
        const firstField = displayFields[0];
        if (firstField && item[firstField]) {
            return `${item[firstField]}`;
        }
        return String(item[primaryKeyField] || '');
    }, [displayTemplate, displayFields, primaryKeyField]);

    // Handle selection
    const handleSelect = (item: Record<string, unknown>) => {
        onChange(item[primaryKeyField] as string | number);
        setSheetOpen(false);
        setSearch('');
        setPage(1);
    };

    // Handle clear
    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange(null);
    };

    // Get display text for current value
    const displayText = selectedItem ? formatDisplayValue(selectedItem) : '';

    return (
        <>
            {/* Trigger Button */}
            <div
                className={cn(
                    'flex items-center gap-2 rounded-md border px-3 py-2 transition-colors',
                    disabled ? 'cursor-not-allowed bg-muted' : 'cursor-pointer hover:bg-accent',
                    className
                )}
                onClick={() => !disabled && setSheetOpen(true)}
            >
                <div className="flex-1 truncate">
                    {displayText ? (
                        <span className="text-sm">{displayText}</span>
                    ) : (
                        <span className="text-sm text-muted-foreground">{placeholder}</span>
                    )}
                </div>
                <div className="flex items-center gap-1">
                    {value && !disabled && (
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={handleClear}
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    )}
                    {enableLink && value && (
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={(e) => {
                                e.stopPropagation();
                                window.open(`/admin/content/${relatedCollection}/${value}`, '_blank');
                            }}
                        >
                            <ExternalLink className="h-4 w-4" />
                        </Button>
                    )}
                    <Edit className="h-4 w-4 text-muted-foreground" />
                </div>
            </div>

            {/* Selection Sheet */}
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                <SheetContent side="right" className="w-full sm:max-w-xl">
                    <SheetHeader>
                        <SheetTitle>
                            Select {collectionInfo?.meta?.collection || relatedCollection}
                        </SheetTitle>
                        <SheetDescription>
                            Choose an item from the {relatedCollection} collection
                        </SheetDescription>
                    </SheetHeader>

                    <div className="mt-4 space-y-4">
                        {/* Search */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                placeholder="Search..."
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setPage(1);
                                }}
                                className="pl-10"
                            />
                        </div>

                        {/* Items Table */}
                        <ScrollArea className="h-[calc(100vh-280px)]">
                            {itemsLoading || fieldsLoading ? (
                                <div className="space-y-2">
                                    {Array.from({ length: 5 }).map((_, i) => (
                                        <Skeleton key={i} className="h-12 w-full" />
                                    ))}
                                </div>
                            ) : items.length === 0 ? (
                                <div className="flex h-32 items-center justify-center text-muted-foreground">
                                    No items found
                                </div>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-16">{primaryKeyField}</TableHead>
                                            {displayFields.map((field) => (
                                                <TableHead key={field}>{field}</TableHead>
                                            ))}
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {items.map((item: Record<string, unknown>) => (
                                            <TableRow
                                                key={String(item[primaryKeyField])}
                                                className={cn(
                                                    'cursor-pointer',
                                                    item[primaryKeyField] === value && 'bg-accent'
                                                )}
                                                onClick={() => handleSelect(item)}
                                            >
                                                <TableCell className="font-medium">
                                                    {String(item[primaryKeyField])}
                                                </TableCell>
                                                {displayFields.map((field) => (
                                                    <TableCell key={field}>
                                                        {item[field] != null ? String(item[field]) : '-'}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </ScrollArea>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-between border-t pt-4">
                                <span className="text-sm text-muted-foreground">
                                    Page {page} of {totalPages} ({totalCount} items)
                                </span>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setPage(p => Math.max(1, p - 1))}
                                        disabled={page === 1}
                                    >
                                        <ChevronLeft className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                        disabled={page === totalPages}
                                    >
                                        <ChevronRight className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Actions */}
                        <div className="flex gap-2 border-t pt-4">
                            {value && (
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        onChange(null);
                                        setSheetOpen(false);
                                    }}
                                >
                                    Clear Selection
                                </Button>
                            )}
                            <Button
                                variant="outline"
                                className="ml-auto"
                                onClick={() => setSheetOpen(false)}
                            >
                                Cancel
                            </Button>
                        </div>
                    </div>
                </SheetContent>
            </Sheet>
        </>
    );
}
